import React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@/components/common/Tooltip";

export interface SegmentedTabsOption<T extends string> {
  value: T;
  label: React.ReactNode;
  tooltip?: string;
}

interface Props<T extends string> {
  options: readonly SegmentedTabsOption<T>[];
  value: T;
  onChange: (v: T) => void;
  /** Label rendered above the control. Omit for an unlabelled tab strip. */
  label?: string;
  /// What the choice decides, on a hover mark against the label.
  info?: string;
  /** Lets the options break onto a second line instead of overflowing. */
  wrap?: boolean;
}

/// Full-width tabbed segmented control. Per-option tooltips, sized to
/// fit `item-height` rows in forms. Distinct from `SegmentedControl`,
/// which is the compact inline variant used in column-format pickers.
export function SegmentedTabs<T extends string>({
  options,
  value,
  onChange,
  label,
  info,
  wrap = false,
}: Props<T>) {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      {label && (
        <span className="flex items-center gap-1">
          <span className="text-fg-muted-medium">{label}</span>
          {info && (
            <Tooltip title={info}>
              <InfoOutlinedIcon
                sx={{ fontSize: 14 }}
                className="text-fg-muted-medium cursor-help"
                aria-label="About this choice"
              />
            </Tooltip>
          )}
        </span>
      )}
      <div
        className={`flex w-full rounded-app border border-border overflow-hidden divide-x divide-border ${wrap ? "flex-wrap" : ""}`}
      >
        {options.map((opt) => {
          const selected = opt.value === value;
          const button = (
            <button
              type="button"
              onClick={() => onChange(opt.value)}
              aria-pressed={selected}
              className={`item-height px-4 inline-flex items-center justify-center whitespace-nowrap transition-colors cursor-pointer flex-1 ${
                selected
                  ? "bg-list-selected-light text-fg-semibold"
                  : "bg-bg text-fg-muted-medium hover:text-fg"
              }`}
            >
              {opt.label}
            </button>
          );
          return opt.tooltip ? (
            <Tooltip key={opt.value} title={opt.tooltip}>
              {button}
            </Tooltip>
          ) : (
            <React.Fragment key={opt.value}>{button}</React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
